import { CSSProperties, useState, useRef, useEffect } from 'react';
import { useHistory } from 'react-router-dom';
import { clsx, styx } from 'modules/Utils';
import { NavigationItem } from './Navbar';
import symphonyLogo from '../../assets/images/symphonyLogo.svg';
import Colors from 'modules/Colors';
import { getNavigationPlaceholderImage } from 'modules/Const';
import ButtonSymphony from 'elements/ButtonSymphony';
import useScreen from 'Hooks/useScreen';
import Icon from 'elements/Icon';

interface PublicDesktopNavigationProps {
    className: string;
    navigationItems: NavigationItem[];
}

const PublicDesktopNavigation = ({
    className,
    navigationItems,
}: PublicDesktopNavigationProps) => {
    const history = useHistory();
    const { mobileView } = useScreen();
    const [openDropdown, setOpenDropdown] = useState<string | null>(null);
    const [hoveredItem, setHoveredItem] = useState<string | null>(null);
    const dropdownRef = useRef<HTMLDivElement>(null);

    // Filter navigation items for public desktop menu
    const publicNavigationItems = navigationItems.filter(
        (navItem: NavigationItem) =>
            (navItem.isPublic || navItem.isAvailableAlways) && navItem.showOnDesktopMainMenu
    );

    // Handle clicks outside dropdown to close it
    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
                setOpenDropdown(null);
            }
        };

        document.addEventListener('mousedown', handleClickOutside);
        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, []);

    const handleLogoClick = () => {
        history.push('/');
    };

    const handleNavItemClick = (navItem: NavigationItem) => {
        if (navItem.dropdownItems && navItem.dropdownItems.length > 0) {
            setOpenDropdown(openDropdown === navItem.name ? null : navItem.name);
        } else {
            if (navItem.onClick) {
                navItem.onClick({} as any);
            } else if (navItem.href) {
                history.push(navItem.href);
            }
            setOpenDropdown(null);
        }
    };

    const handleDropdownItemClick = (dropdownItem: NavigationItem) => {
        if (dropdownItem.onClick) {
            dropdownItem.onClick({} as any);
        } else if (dropdownItem.href) {
            history.push(dropdownItem.href);
        }
        setOpenDropdown(null);
    };



    const renderLogo = () => {
        return (
            <div
                className="publicDesktopNavigationLogo"
                style={sty.logo}
                onClick={handleLogoClick}
            >
                <img
                    className="symphonyLogoPublicDesktop"
                    src={symphonyLogo}
                    alt="Symphony Logo"
                    style={sty.logoImage}
                />
            </div>
        );
    };

    const renderDropdown = (navItem: NavigationItem) => {
        if (!navItem.dropdownItems || openDropdown !== navItem.name) {
            return null;
        }

        // Render Learn dropdown as simple links
        if (navItem.name === 'Learn') {
            return (
                <div
                    className="publicDesktopNavigationDropdown"
                    style={sty.dropdown}
                    ref={dropdownRef}
                >
                    <div className="publicDesktopNavigationDropdownContent" style={sty.simpleDropdownContent}>
                        {navItem.dropdownItems.map((dropdownItem: NavigationItem) => (
                            <div
                                key={dropdownItem.name}
                                className="publicDesktopNavigationSimpleDropdownItem"
                                style={{
                                    ...sty.simpleDropdownItem,
                                    backgroundColor: hoveredItem === dropdownItem.name ? Colors.greyLight : 'transparent',
                                }}
                                onMouseEnter={() => setHoveredItem(dropdownItem.name)}
                                onMouseLeave={() => setHoveredItem(null)}
                                onClick={() => handleDropdownItemClick(dropdownItem)}
                            >
                                {dropdownItem.name}
                            </div>
                        ))}
                    </div>
                </div>
            );
        }

        // Default box-style dropdown for Platform and other items
        return (
            <div
                className="publicDesktopNavigationDropdown"
                style={sty.dropdown}
                ref={dropdownRef}
            >
                <div className="publicDesktopNavigationDropdownContent" style={sty.dropdownContent}>
                    {navItem.dropdownItems.map((dropdownItem: NavigationItem) => (
                        <div
                            key={dropdownItem.name}
                            className="publicDesktopNavigationDropdownItem"
                            style={sty.dropdownItem}
                            onClick={() => handleDropdownItemClick(dropdownItem)}
                        >
                            <div className="dropdownItemImageContainer" style={sty.dropdownItemImageContainer}>
                                <img
                                    src={getNavigationPlaceholderImage(dropdownItem.name, 'desktop')}
                                    alt={dropdownItem.name}
                                    style={sty.dropdownItemImage}
                                />
                            </div>
                            <div className="dropdownItemTitle" style={sty.dropdownItemTitle}>
                                {dropdownItem.name}
                            </div>
                        </div>
                    ))}
                </div>
            </div>
        );
    };

    const renderNavigationItems = () => {
        // Separate navigation items by type
        const mainNavItems = publicNavigationItems.filter(
            item => item.name !== 'Get Started' && item.name !== 'Log In'
        );
        const authNavItems = publicNavigationItems.filter(
            item => item.name === 'Get Started' || item.name === 'Log In'
        );

        return (
            <>
                <div className="publicDesktopNavigationCenter" style={sty.centerNav}>
                    {mainNavItems.map((navItem: NavigationItem) => {
                        const hasDropdown = navItem.dropdownItems && navItem.dropdownItems.length > 0;
                        const isDropdownOpen = openDropdown === navItem.name;

                        return (
                            <div key={navItem.name} className="publicDesktopNavigationItemContainer" style={sty.navItemContainer}>
                                <div
                                    className="publicDesktopNavigationItem"
                                    style={{
                                        ...sty.navItem,
                                        ...(isDropdownOpen ? sty.navItemActive : {}),
                                    }}
                                    onClick={() => handleNavItemClick(navItem)}
                                >
                                    {navItem.name}
                                    {hasDropdown && (
                                        <Icon
                                            className="publicDesktopNavigationDropdownIcon"
                                            icon="chevronDown"
                                            size={12}
                                            color={Colors.white}
                                            style={{
                                                marginLeft: 4,
                                                transform: isDropdownOpen ? 'rotate(180deg)' : 'rotate(0deg)',
                                                transition: 'transform 0.2s ease',
                                            }}
                                        />
                                    )}
                                </div>
                                {renderDropdown(navItem)}
                            </div>
                        );
                    })}
                </div>
                <div className="publicDesktopNavigationRight" style={sty.rightNav}>
                    {authNavItems.map((navItem: NavigationItem) => {
                        const isGetStarted = navItem.name === 'Get Started';
                        return (
                            <ButtonSymphony
                                key={navItem.name}
                                className={`publicDesktopNavigationButton`}
                                style={sty.publicDesktopNavigationButton}
                                onClick={() => handleNavItemClick(navItem)}
                                variant={isGetStarted ? 'primary' : 'outlined'}
                                size="medium"
                            >
                                {navItem.name}
                            </ButtonSymphony>
                        );
                    })}
                </div>
            </>
        );
    };

    return (
        <div
            className={clsx('publicDesktopNavigationComponent', className)}
            style={sty.container}
        >
            {renderLogo()}
            {renderNavigationItems()}
        </div>
    );
};

const sty: Record<string, CSSProperties> = {
    container: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        backgroundColor: Colors.purpleDark,
        padding: '10px 24px',
        position: 'relative',
    },
    logo: {
        cursor: 'pointer',
        display: 'flex',
        alignItems: 'center',
    },
    logoImage: {
        height: 18,
        width: 'auto',
    },
    centerNav: {
        display: 'flex',
        justifyContent: 'flex-end',
        gap: 12,
        alignItems: 'center',
        flex: 1,
        marginRight: 12,
    },
    navItemContainer: {
        position: 'relative',
        display: 'flex',
        flexDirection: 'column',
    },
    navItem: {
        color: Colors.white,
        fontSize: 16,
        fontWeight: 400,
        cursor: 'pointer',
        padding: '8px 16px',
        borderRadius: 8,
        transition: 'all 0.2s ease',
        display: 'flex',
        alignItems: 'center',
    },
    navItemActive: {
        backgroundColor: 'rgba(255, 255, 255, 0.1)',
    },
    rightNav: {
        display: 'flex',
        alignItems: 'center',
        gap: 12,
    },
    publicDesktopNavigationButton: {
        fontSize: 16,
    },
    dropdown: {
        position: 'absolute',
        top: '100%',
        left: '50%',
        transform: 'translateX(-50%)',
        zIndex: 1000,
        marginTop: 8,
    },
    dropdownContent: {
        backgroundColor: Colors.white,
        borderRadius: 12,
        padding: 16,
        boxShadow: '0 10px 25px rgba(0, 0, 0, 0.15)',
        border: `1px solid ${Colors.greyLight}`,
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))',
        gap: 12,
        width: 600,
    },
    dropdownItem: {
        padding: 16,
        borderRadius: 8,
        border: `1px solid ${Colors.greyLight}`,
        cursor: 'pointer',
        transition: 'all 0.2s ease',
        backgroundColor: Colors.white,
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        textAlign: 'center',
        minHeight: 140,
    },
    dropdownItemImageContainer: {
        marginBottom: 12,
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        width: '100%',
        height: '100%'
    },
    dropdownItemImage: {
        width: '100%',
        height: '100%',
        objectFit: 'cover',
        borderRadius: 6,
    },
    dropdownItemTitle: {
        fontSize: 14,
        fontWeight: 500,
        color: Colors.grey,
        lineHeight: 1.3,
    },
    simpleDropdownContent: {
        backgroundColor: Colors.white,
        borderRadius: 8,
        padding: 8,
        boxShadow: '0 10px 25px rgba(0, 0, 0, 0.15)',
        border: `1px solid ${Colors.greyLight}`,
        display: 'flex',
        flexDirection: 'column',
        minWidth: 200,
        width: 'auto',
    },
    simpleDropdownItem: {
        padding: '12px 16px',
        cursor: 'pointer',
        transition: 'all 0.2s ease',
        fontSize: 14,
        fontWeight: 400,
        color: Colors.grey,
        borderRadius: 4,
        textAlign: 'left',
    },
};

export default PublicDesktopNavigation; 